/**
 * AdminAnalyticsPage.js — /admin/analytics
 *
 * RC 1.47.ADMIN-ANALYTICS (Phase 1, 2026-05-02).
 *
 * Operator-grade dashboard combining product telemetry from
 * `GET /api/admin/analytics/overview` with a link out to the
 * dedicated Scoring Governance panel. One round-trip on mount,
 * one on Refresh.
 *
 * Sections (top → bottom):
 *   - Headlines   — total jobs, analyses, variants, packs, costs
 *   - Scoring     — analyse-fit decision histogram + reject rate
 *   - Tailoring   — funnel + average uplift + top-performing CV style
 *   - Packs       — pack-status breakdown
 *   - NDOR cost   — call volume + USD spend
 *   - Rubric      — rollout health (mirrors AdminScoringPage badge)
 *
 * Hard rails:
 *   - Read-only.
 *   - No chart library — pure CSS bars/donuts to keep bundle slim.
 *   - Empty data degrades to "—" rather than zero noise.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, BarChart3, FileText, Sparkles, Wallet,
  ShieldCheck, ArrowUpRight, Target,
} from 'lucide-react';

function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-GB').format(Number(n));
}

function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtUsd(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 4,
  }).format(Number(n));
}

function fmtScore(n) {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  // `fit_score` is stored 0-1, `cv_sub_score` is 0-100. We surface
  // both at integer-ish precision since this is a glance dashboard.
  return v < 1 ? v.toFixed(3) : v.toFixed(1);
}

function HeadlineTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="border-ndor-edge" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
          {Icon ? <Icon className="h-3 w-3" /> : null}
          {label}
        </CardDescription>
        <CardTitle className="text-2xl font-mono-ui tabular-nums">{value}</CardTitle>
        {sub ? <p className="text-[11px] text-ndor-ink-muted leading-relaxed">{sub}</p> : null}
      </CardHeader>
    </Card>
  );
}

const DECISION_LABELS = {
  shortlist:                'Shortlist',
  apply_with_repositioning: 'Tailor',
  hold:                     'Hold',
  reject:                   'Reject',
  unanalysed:               'Unanalysed',
};
const DECISION_COLORS = {
  shortlist:                'bg-emerald-500',
  apply_with_repositioning: 'bg-amber-500',
  hold:                     'bg-slate-400',
  reject:                   'bg-rose-500',
  unanalysed:               'bg-ndor-edge',
};

export default function AdminAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const d = await apiFetch('/api/admin/analytics/overview');
      setData(d);
    } catch (err) {
      toast.error(err?.message || 'Failed to load analytics overview.');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decisionTotal = useMemo(() => {
    const h = data?.scoring?.decision_histogram;
    if (!h) return 0;
    return Object.values(h).reduce((a, b) => a + b, 0);
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-ndor-ink-secondary py-12 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading analytics overview…
      </div>
    );
  }

  const headlines = data?.headlines || {};
  const scoring   = data?.scoring   || {};
  const tailoring = data?.tailoring || {};
  const packs     = data?.packs     || {};
  const ndor      = data?.ndor      || {};
  const rubric    = data?.rubric    || {};
  // RC 1.48.7 — Value-guard audit tile. Populated with zero-seeded
  // histogram so the tile renders cleanly even when the backend
  // returns the key empty.
  const valueGuard = data?.value_guard || {
    window_days: 30,
    total_attempts: 0,
    reason_histogram: {},
    reason_pct: {},
    estimated_credits_saved: null,
    cv_improve_price: null,
  };

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ndor-ink leading-tight">Operator Analytics</h1>
          <p className="text-sm text-ndor-ink-secondary leading-relaxed max-w-2xl">
            Read-only product telemetry: candidate funnel, scoring posture,
            tailoring uplift, NDOR call cost, rubric rollout. Numbers update
            on Refresh.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" size="sm" data-testid="admin-analytics-link-scoring">
            <Link to="/admin/scoring">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Scoring Governance
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={refreshing}
            data-testid="admin-analytics-refresh"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Headlines band */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <HeadlineTile
          icon={Target}
          label="Jobs tracked"
          value={fmtNum(headlines.total_jobs)}
          sub="Across all source kinds, hidden rows excluded."
          testId="admin-analytics-tile-jobs"
        />
        <HeadlineTile
          icon={BarChart3}
          label="Analyse-Fit runs"
          value={fmtNum(headlines.total_analyses)}
          sub={`Avg holistic fit ${fmtScore(headlines.avg_holistic_fit)} · avg strict ${fmtScore(headlines.avg_source_strict)}`}
          testId="admin-analytics-tile-analyses"
        />
        <HeadlineTile
          icon={Sparkles}
          label="CV variants"
          value={fmtNum(headlines.total_variants)}
          sub={`Avg uplift ${headlines.avg_variant_uplift !== null ? `+${headlines.avg_variant_uplift}` : '—'} pts`}
          testId="admin-analytics-tile-variants"
        />
        <HeadlineTile
          icon={FileText}
          label="Application packs"
          value={fmtNum(headlines.total_packs)}
          sub={`Reject rate ${fmtPct(headlines.reject_rate_pct)}`}
          testId="admin-analytics-tile-packs"
        />
      </div>

      {/* Scoring decision mix */}
      <Card className="border-ndor-edge">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Analyse-Fit decision mix</CardTitle>
          <CardDescription className="text-xs">
            Across {fmtNum(decisionTotal)} canonical Source-CV analyses.
            Variant rows excluded — they're separate strict-grade runs.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(DECISION_LABELS).map(([k, label]) => {
            const n = scoring?.decision_histogram?.[k] ?? 0;
            const pct = decisionTotal > 0 ? (n * 100 / decisionTotal) : 0;
            return (
              <div key={k} className="space-y-1" data-testid={`admin-analytics-decision-${k}`}>
                <div className="flex items-center justify-between text-xs font-medium text-ndor-ink">
                  <span>{label}</span>
                  <span className="font-mono-ui tabular-nums text-ndor-ink-secondary">
                    {fmtNum(n)} · {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-2 rounded-full bg-ndor-edge overflow-hidden">
                  <div
                    className={`h-2 ${DECISION_COLORS[k]} transition-all`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Tailoring funnel */}
      <Card className="border-ndor-edge">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tailoring funnel</CardTitle>
          <CardDescription className="text-xs">
            How candidates move from analyse-fit → variant → packed application.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <FunnelStep
              label="Analyses run"
              value={tailoring?.funnel?.analyses_run}
              testId="admin-analytics-funnel-analyses"
            />
            <FunnelStep
              label="Variants generated"
              value={tailoring?.funnel?.variants_generated}
              parent={tailoring?.funnel?.analyses_run}
              testId="admin-analytics-funnel-variants"
            />
            <FunnelStep
              label="Packs created"
              value={tailoring?.funnel?.packs_created}
              parent={tailoring?.funnel?.analyses_run}
              testId="admin-analytics-funnel-packs"
            />
          </div>
          {tailoring?.top_template ? (
            <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-xs leading-relaxed text-emerald-900" data-testid="admin-analytics-top-template">
              <strong>Top-performing template:</strong> {tailoring.top_template.template_type}
              {' · '}avg strict score <span className="font-mono-ui tabular-nums">{tailoring.top_template.avg_strict_score}</span>
              {' over '}{tailoring.top_template.sample_count} samples.
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Pack mix + NDOR cost side-by-side */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="border-ndor-edge">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Application-pack status</CardTitle>
            <CardDescription className="text-xs">
              Across {fmtNum(packs?.total)} packs.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1">
            {Object.entries(packs?.status_histogram || {}).length === 0 ? (
              <p className="text-sm text-ndor-ink-muted" data-testid="admin-analytics-packs-empty">No packs yet.</p>
            ) : (
              Object.entries(packs.status_histogram).map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-xs">
                  <span className="capitalize">{s.replace(/_/g, ' ')}</span>
                  <span className="font-mono-ui tabular-nums" data-testid={`admin-analytics-pack-status-${s}`}>{fmtNum(n)}</span>
                </div>
              ))
            )}
            <div className="pt-2 border-t border-ndor-edge mt-2 grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted">With variant CV</p>
                <p className="font-mono-ui tabular-nums" data-testid="admin-analytics-pack-with-variant">{fmtNum(packs?.with_variant)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted">With cover letter</p>
                <p className="font-mono-ui tabular-nums" data-testid="admin-analytics-pack-with-cover">{fmtNum(packs?.with_cover_letter)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-ndor-edge">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              NDOR cost
              <Badge className="bg-ndor-surface-alt text-slate-200 border-slate-200" variant="outline">USD</Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              NDOR-reported cost across {fmtNum(ndor?.total_calls)} calls.
              NDORFlow does not branch on this — operator visibility only.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted">Total cost</p>
              <p className="text-2xl font-mono-ui tabular-nums" data-testid="admin-analytics-ndor-total-cost">
                {fmtUsd(ndor?.total_cost_usd)}
              </p>
            </div>
            {Object.entries(ndor?.by_task || {}).length > 0 ? (
              <div className="pt-2 border-t border-ndor-edge mt-2 space-y-1 text-xs">
                {Object.entries(ndor.by_task).map(([t, n]) => (
                  <div key={t} className="flex items-center justify-between">
                    <span className="capitalize">{t.replace(/_/g, ' ')}</span>
                    <span className="font-mono-ui tabular-nums" data-testid={`admin-analytics-ndor-task-${t}`}>{fmtNum(n)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* RC 1.48.7 — Value-Guard audit tile. Surfaces RC 1.48.6
          charge-gate telemetry (which branches fired this billing
          cycle + Credits protected). Data source: the `value_guard`
          block on GET /api/admin/analytics/overview. */}
      <Card className="border-ndor-edge" data-testid="admin-analytics-value-guard-card">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
            <div>
              <CardTitle className="text-base">Value-Guard audit · last {valueGuard.window_days} days</CardTitle>
              <CardDescription className="text-xs">
                Charge-gate branches fired across all CV generations. Surfaces whether the RC 1.48.6 high-CV 50% presentation branch is absorbing the right volume.
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-[10px] font-mono-ui uppercase tracking-[0.14em] shrink-0">
              <span data-testid="admin-analytics-value-guard-total">{fmtNum(valueGuard.total_attempts)}</span>
              <span className="ml-1 text-ndor-ink-muted">attempts</span>
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {valueGuard.total_attempts === 0 ? (
            <p className="text-sm text-ndor-ink-muted" data-testid="admin-analytics-value-guard-empty">
              No charge events in this window yet. Counters will populate as candidates generate variants.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {[
                { key: 'cv_quality_improved',               label: 'Material uplift',       sub: '100% charged', tone: 'bg-emerald-500/10 border-emerald-500/40' },
                { key: 'presentation_optimisation_high_cv', label: 'Presentation only',     sub: '50% charged',  tone: 'bg-amber-500/10 border-amber-500/40' },
                { key: 'within_tolerance_band',             label: 'Within tolerance',      sub: 'Waived',       tone: 'bg-sky-500/10 border-sky-500/40' },
                { key: 'cv_quality_no_improvement',         label: 'No uplift',             sub: 'Waived',       tone: 'bg-sky-500/10 border-sky-500/40' },
                { key: 'no_baseline_protection',            label: 'No baseline',           sub: '100% charged', tone: 'bg-ndor-surface-alt border-slate-200' },
              ].map((row) => (
                <div
                  key={row.key}
                  className={`rounded-md border px-2.5 py-2 ${row.tone}`}
                  data-testid={`admin-analytics-value-guard-row-${row.key}`}
                >
                  <p className="text-[10px] font-mono-ui uppercase tracking-[0.14em] text-ndor-ink-muted leading-tight">
                    {row.label}
                  </p>
                  <p className="text-[9px] text-ndor-ink-muted mt-0.5">{row.sub}</p>
                  <p className="mt-1.5 text-lg font-mono-ui tabular-nums text-ndor-ink">
                    <span data-testid={`admin-analytics-value-guard-count-${row.key}`}>
                      {fmtNum(valueGuard.reason_histogram?.[row.key] || 0)}
                    </span>
                    <span className="text-[10px] text-ndor-ink-muted ml-1.5 font-normal">
                      (<span data-testid={`admin-analytics-value-guard-pct-${row.key}`}>
                        {fmtPct(valueGuard.reason_pct?.[row.key])}
                      </span>)
                    </span>
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2">
            <p className="text-xs text-ndor-ink-secondary leading-relaxed">
              Estimated Credits protected by waives + 50% presentation discounts
              {valueGuard.cv_improve_price
                ? <> (using current <code>cv_improve</code> price = {fmtNum(valueGuard.cv_improve_price)} Credits)</>
                : <> (price not configured — saved value unavailable)</>}.
            </p>
            <p className="text-2xl font-mono-ui tabular-nums text-ndor-ink shrink-0" data-testid="admin-analytics-value-guard-credits-saved">
              {valueGuard.estimated_credits_saved === null || valueGuard.estimated_credits_saved === undefined
                ? '—'
                : `${fmtNum(valueGuard.estimated_credits_saved)} Cr`}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rubric rollout (mini, links to dedicated page) */}
      <Card className="border-ndor-edge" data-testid="admin-analytics-rubric-card">
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base">Rubric rollout</CardTitle>
            <CardDescription className="text-xs">
              <code>{rubric?.rubric_version}</code> · <code>{rubric?.scoring_model_version}</code> · {fmtNum(rubric?.current_row_count)} of {fmtNum(rubric?.total_row_count)} rows on current
            </CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/scoring" data-testid="admin-analytics-rubric-link">
              Open Scoring panel <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          <div className="h-2 rounded-full bg-ndor-edge overflow-hidden">
            <div
              className="h-2 bg-emerald-500"
              style={{ width: `${100 - (rubric?.stale_pct ?? 0)}%` }}
              data-testid="admin-analytics-rubric-bar"
            />
          </div>
          <p className="mt-1 text-[10px] font-mono-ui uppercase tracking-[0.14em] text-ndor-ink-muted">
            {rubric?.stale_pct ?? 0}% stale · auto-refresh on next analyse-fit
          </p>
        </CardContent>
      </Card>

      <p className="text-[10px] text-ndor-ink-muted font-mono-ui leading-relaxed">
        Source: <code>GET /api/admin/analytics/overview</code> · RC 1.47.ADMIN-ANALYTICS · read-only
      </p>
    </div>
  );
}

function FunnelStep({ label, value, parent, testId }) {
  const pct = (parent && Number(parent) > 0) ? (Number(value) * 100 / Number(parent)) : null;
  return (
    <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 px-3 py-3" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-ndor-ink-muted font-mono-ui">{label}</p>
      <p className="text-2xl font-mono-ui tabular-nums text-ndor-ink mt-0.5">{fmtNum(value)}</p>
      {pct !== null ? (
        <p className="text-[11px] text-ndor-ink-muted mt-0.5">
          {pct.toFixed(1)}% of analyses
        </p>
      ) : null}
    </div>
  );
}
